import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import Mpegts from "mpegts.js";
import ReplayIcon from "@mui/icons-material/Replay";

interface IUNVCameraStreamProps {
	cameraId: number;
}

const UNVCameraStream: FC<IUNVCameraStreamProps> = (props) => {
	const { cameraId } = props;

	const videoWrapperRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const playerRef = useRef<Mpegts.Player | null>(null);
	const intervalRef = useRef<NodeJS.Timer | null>(null);

	const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
	const [streamError, setStreamError] = useState<string>("");
	const [showLoader, setShowLoader] = useState<boolean>(true);

	function getWebsocketURL(camera: number) {
		const protocol = `ws${window.location.protocol.replace("http", "")}`;
		const host = window.location.hostname;
		const port = "";
		const endpoint = "/dws/video/socket_stream";

		let url = `${protocol}//${host}`;
		if (port) url += `:${port}`;
		url += `${endpoint}/${camera}/`;

		return url;
	}

	const connectWebsocket = useCallback(() => {
		if (videoRef.current && cameraId) {
			setShowLoader(true);

			if (playerRef.current) {
				playerRef.current.pause();
				playerRef.current.unload();
				playerRef.current.detachMediaElement();
				playerRef.current.destroy();
				playerRef.current = null;
			}

			const websocketURL = getWebsocketURL(cameraId);

			if (Mpegts.isSupported()) {
				playerRef.current = Mpegts.createPlayer(
					{
						type: "flv",
						url: websocketURL,
						cors: true
					},
					{
						isLive: true,
						enableStashBuffer: true,
						stashInitialSize: 2048,
						lazyLoad: false,
						liveSync: true,
						liveSyncPlaybackRate: 1.05,
						statisticsInfoReportInterval: 40,
						seekType: "range"
					}
				);

				playerRef.current.on(Mpegts.Events.ERROR, (_error, errorType, errorDetails) => {
					if (errorType && errorType === Mpegts.ErrorDetails.MEDIA_FORMAT_UNSUPPORTED) {
						setStreamError("Failed to start stream");
						setShowLoader(false);
					} else if (errorDetails && errorDetails.msg === "Unsupported WebSocket message type: String") {
						setStreamError("Camera connection terminated");
						setShowLoader(false);
					} else if (errorType && errorType === Mpegts.ErrorDetails.MEDIA_MSE_ERROR) {
						connectWebsocket();
					}
				});

				playerRef.current.on(Mpegts.Events.STATISTICS_INFO, (info) => {
					if (info && typeof info.speed === "number" && info.speed > 0) {
						setShowLoader(false);
					}

					if (playerRef.current && playerRef.current.buffered.length > 0) {
						const currentTime = playerRef.current.buffered.end(0) - 0.1;
						if (Math.abs(playerRef.current.currentTime - currentTime) > 1) {
							playerRef.current.currentTime = currentTime;
						}
					}
				});

				playerRef.current.attachMediaElement(videoRef.current);
				playerRef.current.load();
				playerRef.current.play();
			}
		}
	}, [cameraId, videoRef]);

	function handleRetryButtonClick() {
		setStreamError("");
		connectWebsocket();
	}

	useEffect(() => {
		if (videoWrapperRef.current) {
			const { offsetWidth: videoWidth, offsetHeight: videoHeight } = videoWrapperRef.current;

			if (videoWidth && videoHeight) {
				let width = videoWidth;
				let height = videoHeight;

				if (width / height > 16 / 9) {
					height = videoHeight;
					width = (height * 16) / 9;
				} else {
					width = videoWidth;
					height = (width * 9) / 16;
				}

				setVideoDimensions({ width, height });
			}
		}
	}, [videoWrapperRef.current?.offsetWidth, videoWrapperRef.current?.offsetHeight]);

	useEffect(() => {
		connectWebsocket();

		intervalRef.current = setInterval(() => {
			if (playerRef.current) {
				if (playerRef.current.buffered.length > 0) {
					let currentTime = playerRef.current.buffered.end(0) - 0.1;
					if (Math.abs(playerRef.current.currentTime - currentTime) > 1) {
						playerRef.current.currentTime = currentTime;
					}
				}
			}
		}, 5000);

		return () => {
			if (playerRef.current) {
				playerRef.current.pause();
				playerRef.current.unload();
				playerRef.current.detachMediaElement();
				playerRef.current.destroy();
				playerRef.current = null;
			}

			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [connectWebsocket]);

	return (
		<Box className="stream-canvas-wrapper" ref={videoWrapperRef}>
			<video autoPlay id="stream-video" width={videoDimensions.width} height={videoDimensions.height} ref={videoRef} />

			{/* {!playerRef.current ? (
				<Box className="stream-canvas-overlay">
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReplayIcon />}
						onClick={connectWebsocket}
					>
						Start Stream
					</Button>
				</Box>
			) : null} */}

			{streamError ? (
				<Box className="stream-canvas-overlay">
					<Typography>{streamError}</Typography>

					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReplayIcon />}
						onClick={handleRetryButtonClick}
					>
						Retry
					</Button>
				</Box>
			) : null}

			{showLoader ? (
				<Box className="stream-canvas-overlay">
					<CircularProgress />
				</Box>
			) : null}
		</Box>
	);
};

export default UNVCameraStream;
